/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { Profiler, useContext, useEffect, useMemo, useState } from "react";
import { matchPath, Route, useHistory, useLocation, useParams } from "react-router-dom";

import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import { I18nChildContext, t, useI18n } from "@bookingcom/lingojs-react";

import { HiddenVisually, Text, useTheme } from "@bookingcom/bui-react";

import { FlightDetailsDesktop, HomeDesktop } from "app/routes";

import { AppShellDesktop } from "../components/elements/AppShell";
import Container from "../components/elements/Container";
import SearchLoadingScreen from "../components/elements/LoadingScreen/components/SearchLoadingScreen";
import SearchResultsDesktop from "../components/elements/SearchResults/SearchResults.desktop";

import { useClientFetch } from "hooks/useClientFetch";
import useClientMetrics from "../hooks/useClientMetrics";
import { useSearchAPIParams } from "hooks/useSearchAPIParams";
import useSearchbox from "../hooks/useSearchbox";

import { getSearchCriteria } from "store/getSearchCriteria";
import { actions as LoaderActions } from "../store/loadingScreen/actions";
import { actions as SearchCriteriaActions } from "../store/searchCriteria/actions";
import { actions as searchResultsActions } from "../store/searchResults/actions";
import { actions as flexibleDateMinPriceActions } from "../store/flexibleDateMinPrice/actions";
// eslint-disable-next-line import/no-deprecated
import { useActions, useStore } from "store";

import { PageHeader } from "./Search";

import { UIClientFetchError, UISearchResults } from "@flights/types";

import useATOL from "hooks/useATOL";
import { useFlightSearchResultsGTMTag } from "hooks/useGTMFlightTags";
import useLocaleContext from "hooks/useLocaleContext";
import useReloadIfSearchStoreMissing, { isSearchStoreInitial } from "hooks/useReloadIfSearchStoreMissing";
import { isOfMetaOrigin } from "utils/marketing-url-params";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";
import useRouteName from "../hooks/useRouteName";
import useTrackPageLoad from "../hooks/useTrackPageLoad";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import StaticContext from "app/StaticContext/StaticContext";
import useRemoveExternalParamsFromUrl from "../hooks/useRemoveExternalParamsFromUrl";
import useMeasureSearchToFlightDetails from "hooks/useMeasureSearchToFlightDetails";
import { trackMetaChannelStagesV2 } from "utils/experiments/track-meta-landings-v2";
import Frame from "components/elements/Frame";
import { useFlexDatesRectrictedPOS } from "components/elements/FlexibleDateBestPrice/useFlexDatesRectrictedPOS";
import useProfiler from "hooks/useProfiler";
import { useDispatch } from "react-redux";
import { actions as brandedFaresActions } from "../store/brandedFareOffers/actions";
import flights_web_contextualized_sr_loading_desktop from "utils/experiments/funnel/flights_web_contextualized_sr_loading_desktop";
import flights_web_reduce_spacing_sr_ddot from "utils/experiments/funnel/flights_web_reduce_spacing_sr_ddot";
import { goal, stage, trackWithDefaultStage } from "@flights/et-universal";
import usePrefetchFlightDetails from "hooks/usePrefetchFlightDetails";
import useTrackExpPaxHaulTraffic from "hooks/useTrackExpPaxHaulTraffic";
import { usePopularFlightsDateTracking } from "utils/experiments/customer-aquisition/flights_web_cat_popular_flight_sb_dates";
import flights_web_trip_type_dropdown_desktop, {
  useTrackTripTypeDropdownDesktop
} from "utils/experiments/funnel/flights_web_trip_type_dropdown_desktop";

const SearchDesktop = () => {
  const staticContext = useContext(StaticContext);
  const params = useParams<{ airports: string }>();
  // eslint-disable-next-line import/no-deprecated
  const store = useStore();
  const location = useLocation<{ hideLoader: boolean; forceRefetch: boolean }>();
  // eslint-disable-next-line import/no-deprecated
  const actions = useActions(searchResultsActions);
  // eslint-disable-next-line import/no-deprecated
  const flexibleActions = useActions(flexibleDateMinPriceActions);
  const reactRouterHistory = useHistory();
  // eslint-disable-next-line import/no-deprecated
  const loaderActions = useActions(LoaderActions);
  const dispatch = useDispatch();
  const isMetaOrigin = getMarketingTrackingVariable("ext-origin");
  // eslint-disable-next-line import/no-deprecated
  const searchCriteriaActions = useActions(SearchCriteriaActions);
  const theme = useTheme();
  const trackSRContextEventV2 = useTrackPageLoad("search_results");
  const trackSRSkeletonContextEventV2 = useTrackPageLoad("search_results_skeleton");
  const trackSRModalContextEventV2 = useTrackPageLoad("search_results_modal");
  const searchBoxProps = useSearchbox();
  const i18n = useI18n() as I18nChildContext;
  const queryParams = new URLSearchParams(location.search);
  const { isRTL } = useLocaleContext();
  const [preloadDetails, toggle] = useState(false);
  const [previousPath, setPreviousPath] = useState("");
  const { trackRenderTime } = useClientMetrics();
  const fireByeahSearchResultsTag = useFlightSearchResultsGTMTag();
  const { isATolProtectedSR } = useATOL();
  const isRestricedFlexDatePOS = useFlexDatesRectrictedPOS();
  const { trackPaxHaulStages } = useTrackExpPaxHaulTraffic();
  const { preFetchFlightDetails, resetFetchedFlightDetails } = usePrefetchFlightDetails(); // flights_web_prefetch_flight_details
  const popularDestination = !!Number(queryParams.get("popular"));
  const datepickerOpen = !!Number(queryParams.get("open_datepicker"));

  if (process.env.BUILD_TARGET === "server" && staticContext.set) {
    const { getInitialState } = require("../server/screens/search");
    staticContext.set({ getInitialState: getInitialState(queryParams) });
  }
  const { isMeta, isDirect, isDomestic, isInternational, isSolo, isFamily, isCouple, isGroup } = useProfiler();
  const [lastTrackedPageLoad, setLastTrackedPageLoad] = useState("");
  const { visible: loadingScreenVisible } = store.loadingScreen;
  const { onFetchedResults, onSelectFlight } = useMeasureSearchToFlightDetails();
  const { trackBackToIndexFromSR } = usePopularFlightsDateTracking();

  useRemoveExternalParamsFromUrl();

  useEffect(() => {
    trackMetaChannelStagesV2("desktop");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    goal("flights_search_results_page_view");
    goal("tbu_vertical_search_results_view");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const routeName = useRouteName();
  const searchAPIParams = useSearchAPIParams();
  const url = `/api/flights/?${searchAPIParams}`;

  /* start - flights_web_flexible_date_search */
  const urlFlexibleDatePrice = useMemo(() => {
    const params = new URLSearchParams(searchAPIParams);
    params.set("useAggregateCache", "1");
    params.set("compareBestPrice", "1");
    return `/api/search/min-price/?${params.toString()}`;
  }, [searchAPIParams]);
  /* end - flights_web_flexible_date_search */

  // https://jira.booking.com/jira/browse/META-308
  // track browser back/forward buttons
  // but only when user navigates between search results

  useEffect(() => {
    return reactRouterHistory.listen((location) => {
      if (reactRouterHistory.action === "POP") {
        if (
          routeName === "searchresults" &&
          matchPath(location.pathname, { exact: true, path: "/flights/:airports" }) &&
          reactRouterHistory.location.hash !== "#basiclayout" // Without this, the Skiplink is broken on Search Results, this makes sure the page is not reloaded when the user uses the Skiplink ("Skip to main content")
        ) {
          //reload page because the search state in client is more likely different than the url state
          //state is not updated on browser back button, but url is
          window.location.reload();
        }
      }
      setPreviousPath(location.pathname);
    });
  }, [previousPath]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return reactRouterHistory.listen(() => {
      if (reactRouterHistory.action === "POP" && popularDestination) {
        trackBackToIndexFromSR();
      }
    });
  }, [popularDestination, reactRouterHistory, trackBackToIndexFromSR]);

  //delay the load of Search Results in background of Flight Details
  // when user lands on SR with a meta offerToken to temporarily fix an issue on ETG
  const delayFetchBy = !!isMetaOrigin && routeName === "flightdetails" ? 500 : 0;

  // flights_web_tech_pause_sr_loading -- start
  const canPauseSR = useMemo(
    () => routeName === "flightdetails" && store.searchResults.fetchStatus === "initial",
    [routeName, store.searchResults.fetchStatus]
  );
  const isPauseSRVariant = useMemo(
    () => canPauseSR && !!trackWithDefaultStage("flights_web_tech_pause_sr_loading", 1),
    [canPauseSR]
  );
  const [isLandedOnFD, setIsLandedOnFD] = useState<undefined | boolean>(undefined);
  useEffect(() => {
    if (isLandedOnFD !== undefined) return;
    setIsLandedOnFD(routeName === "flightdetails");
  }, [isLandedOnFD, routeName]);
  const hideLoadingScreen = useMemo(() => {
    return isLandedOnFD && !!trackWithDefaultStage("flights_web_tech_pause_sr_loading", 1);
  }, [isLandedOnFD]);
  // flights_web_tech_pause_sr_loading -- end

  useClientFetch(url, {
    delayFetchBy,
    condition: () => {
      return !isPauseSRVariant && !isSearchStoreInitial(store.search);
    },
    fetch: () => {
      // only show loading screen when coming from index / searchbox
      if (!location?.state?.hideLoader && routeName !== "flightdetails") {
        history.replaceState({ hideLoader: false }, document.title);

        loaderActions.show();

        setTimeout(() => {
          loaderActions.hide();
        }, 6000);
      }
      actions.fetch();
      resetFetchedFlightDetails(); // flights_web_prefetch_flight_details
    },
    success: (data: UISearchResults) => {
      searchCriteriaActions.setSearchCriteria(getSearchCriteria(queryParams, store.search));
      if (data.flightOffers.length > 0) {
        data.flightOffers[0].highlightInfo?.highlight &&
          flightsSessionStore.set("highlightedOfferToken", data.flightOffers[0].token);
        data.flightOffers.forEach((offer) => {
          if (offer && !offer.requestableBrandedFares && offer.brandedFareOffers) {
            dispatch(brandedFaresActions.fetchSuccess(offer.brandedFareOffers, offer.token));
          }
        });
      }
      actions.fetchSuccess(data);
      void preFetchFlightDetails(data.flightOffers); // flights_web_prefetch_flight_details
      fireByeahSearchResultsTag(); // fire GTM flight search results tag
      loaderActions.hide();
      onFetchedResults();
    },
    error: (data: UIClientFetchError) => {
      goal("flights_search_results_loading_error");
      loaderActions.hide();
      actions.fetchError(data);
    },
    forceRefetch: location?.state?.forceRefetch || isPauseSRVariant
  });

  /* start - flights_web_flexible_date_search */
  useClientFetch(urlFlexibleDatePrice, {
    delayFetchBy,
    fetch: () => flexibleActions.fetch(),
    success: (data) => flexibleActions.fetchSuccess(data),
    error: (data) => flexibleActions.fetchError(data),
    condition: () => {
      if (isPauseSRVariant) return false;
      if (store.flexibleDateMinPrice.fetchStatus !== "success") {
        return store.search.cabinClass === "ECONOMY" && !isRestricedFlexDatePOS && !isOfMetaOrigin();
      }
      return false;
    },
    forceRefetch: location?.state?.forceRefetch || isPauseSRVariant
  });

  useEffect(() => {
    if (!canPauseSR) return;
    if (isDirect()) stage("flights_web_tech_pause_sr_loading", 2);
    if (isMeta()) stage("flights_web_tech_pause_sr_loading", 3);
    if (isDomestic()) stage("flights_web_tech_pause_sr_loading", 4);
    if (isInternational()) stage("flights_web_tech_pause_sr_loading", 5);
    if (isSolo()) stage("flights_web_tech_pause_sr_loading", 6);
    if (isFamily()) stage("flights_web_tech_pause_sr_loading", 7);
    if (isCouple()) stage("flights_web_tech_pause_sr_loading", 8);
    if (isGroup()) stage("flights_web_tech_pause_sr_loading", 9);
  }, [routeName, isDirect, isMeta, isDomestic, isInternational, isSolo, isFamily, isCouple, isGroup, canPauseSR]);

  const { fetchStatus } = store.searchResults;
  const { searchSegments } = store.search;

  const searchBoxControllerProps = {
    ...searchBoxProps,
    i18n,
    defaultSegmentLength: searchSegments.length,
    isDateDropdownOpen: datepickerOpen
  };
  if (!preloadDetails && fetchStatus === "success") {
    toggle(true);
    FlightDetailsDesktop.preload();
    HomeDesktop.preload();
  }

  const currentSearchType = store.search.type;

  useEffect(() => {
    if (["initial", "loading"].includes(fetchStatus)) {
      if (loadingScreenVisible && lastTrackedPageLoad !== "search_results_modal") {
        trackSRModalContextEventV2();
        setLastTrackedPageLoad("search_results_modal");
      } else if (lastTrackedPageLoad !== "search_results_skeleton") {
        trackSRSkeletonContextEventV2();
        setLastTrackedPageLoad("search_results_skeleton");
      }
    } else if (lastTrackedPageLoad !== "search_results") {
      trackSRContextEventV2();
      setLastTrackedPageLoad("search_results");
    }
  }, [fetchStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  const hideBackground = !!flights_web_reduce_spacing_sr_ddot.trackWithDefaultStage();

  useEffect(() => {
    if (routeName === "searchresults") {
      flights_web_contextualized_sr_loading_desktop.trackStages(currentSearchType);
      if (store.searchResults.fetchStatus === "success") {
        flights_web_contextualized_sr_loading_desktop.goals.sr_fully_fetched();
      }
    }
  }, [routeName, currentSearchType, store.searchResults.fetchStatus]);

  useTrackTripTypeDropdownDesktop();

  useEffect(() => {
    if (store.searchResults.fetchStatus !== "success") return;
    trackPaxHaulStages();
  }, [trackPaxHaulStages, store.searchResults.fetchStatus]);

  if (useReloadIfSearchStoreMissing(store.search)) {
    return null;
  }

  isATolProtectedSR(store?.searchResults?.atolProtectedStatus); // to track stages

  return (
    <Profiler id="screen" onRender={trackRenderTime}>
      <PageHeader segments={searchSegments} />
      <AppShellDesktop>
        <Frame direction="column" grow={1} justifyContent="center" attributes={{ style: { width: "100%" } }}>
          <div style={{ background: hideBackground ? undefined : theme.colors.color_background_base_alt }}>
            <Container pt={6} pb={6}>
              <HiddenVisually>
                <Text tagName="h1">{i18n.trans(t("a11y_flights_sr_search_hidden_heading"))}</Text>
              </HiddenVisually>
              <SearchboxController
                {...searchBoxControllerProps}
                isRTL={isRTL}
                isResponsiveFixes={!!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1)}
                enabledCountriesAndAnywhere={true}
                isDropdownExp={!!flights_web_trip_type_dropdown_desktop.trackWithDefaultStage()}
              >
                {() => <SearchBoxHorizontalDefault />}
              </SearchboxController>
            </Container>
          </div>
          <Container pt={hideBackground ? 0 : 6}>
            <Frame direction="column">
              <SearchResultsDesktop airports={params?.airports} onSelectFlight={onSelectFlight} />
            </Frame>
          </Container>
          <Route key="/flights/:airports/:id" path="/flights/:airports/:id" exact={true}>
            <FlightDetailsDesktop />
          </Route>

          {routeName === "searchresults" &&
            !hideLoadingScreen &&
            !flights_web_contextualized_sr_loading_desktop.trackWithDefaultStage() && <SearchLoadingScreen />}
        </Frame>
      </AppShellDesktop>
    </Profiler>
  );
};

export default React.memo(SearchDesktop);
